/**
 * Waits for a DOM element to appear with optional timeout.
 * Uses MutationObserver to efficiently watch for element creation.
 *
 * @param selector - CSS selector or element ID (without #)
 * @param options - Configuration options
 * @returns Promise that resolves with the found element
 */
export const waitForElement = (
  selector: string,
  options: {
    timeout?: number
    container?: HTMLElement
    useId?: boolean
  } = {},
): Promise<HTMLElement> => {
  const { timeout = 5000, container = document.body, useId = false } = options

  return new Promise((resolve, reject) => {
    // Determine how to find the element
    const findElement = () => {
      if (useId) {
        return document.getElementById(selector)
      }
      return document.querySelector(selector) as HTMLElement | null
    }

    // Check if element already exists
    const existing = findElement()
    if (existing) {
      resolve(existing)
      return
    }

    // eslint-disable-next-line prefer-const
    let timeoutId: NodeJS.Timeout
    // eslint-disable-next-line prefer-const
    let observer: MutationObserver

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (observer) observer.disconnect()
    }

    observer = new MutationObserver(() => {
      const element = findElement()
      if (element) {
        cleanup()
        resolve(element)
      }
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
    })

    timeoutId = setTimeout(() => {
      cleanup()
      const errorMessage = useId
        ? `Element with ID "${selector}" not found within ${timeout}ms`
        : `Element "${selector}" not found within ${timeout}ms`
      reject(new Error(errorMessage))
    }, timeout)
  })
}

/**
 * Convenience function to wait for an element by ID
 */
export const waitForElementById = (
  id: string,
  timeout = 5000,
): Promise<HTMLElement> => {
  return waitForElement(id, { timeout, useId: true })
}
